<script setup lang="ts">
import Product from '~/models/Product/Product'
import { useBasketStore } from '~/stores/basket'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import DataLayer from '~/extensions/DataLayer'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import { transformProductToGtmProduct } from '~/utils/transformProductToGtmProduct'

const props = defineProps<{
    product: Product
    bundledProduct: Product
}>()

const { product, bundledProduct } = toRefs(props)

const priceTotal = computed(() => {
    return product.value.price + bundledProduct.value.price
})

const priceBeforeDiscount = computed(() => {
    return (
        (product.value.priceBeforeDiscount || product.value.price) +
        (bundledProduct.value.priceBeforeDiscount || bundledProduct.value.price)
    )
})

const totalDiscount = computed(() => {
    return priceBeforeDiscount.value - priceTotal.value
})

const productIdReferrer = inject<Ref<number | null> | null>('productIdReferrer', null)

const addToBasket = () => {
    const basketStore = useBasketStore()
    const productAmount = basketStore.productAmounts[product.value.id] || 0
    addProductToBasket({
        product: product.value,
        productId: product.value.id,
        amount: productAmount + 1,
        showPreBasket: true,
        forceShowPreBasket: true,
        itemListName: GtmItemListNameEnum.ProductDetailDynamicBundle,
        listPosition: 0,
        skipPushToDataLayer: true,
    })

    const bundledProductAmount = basketStore.productAmounts[bundledProduct.value.id] || 0
    addProductToBasket({
        product: product.value,
        productId: bundledProduct.value.id,
        amount: bundledProductAmount + 1,
        showPreBasket: false,
        itemListName: GtmItemListNameEnum.ProductDetailDynamicBundle,
        listPosition: 1,
        skipPushToDataLayer: true,
    })

    const ecommerceEvent = {
        type: GtmEcommerceEventTypeEnum.AddToCart,
        products: [transformProductToGtmProduct(product.value), transformProductToGtmProduct(bundledProduct.value)],
        itemListName: 'dynamic_bundles',
        productAmount: 1,
        productIdReferrer: productIdReferrer?.value || null,
    } as GtmEcommerceEvent
    DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)
}
const redirectToUrl = async (event: Event): Promise<void> => {
    setTimeout(() => {
        // do long task in new thread
        const ecommerceEvent = {
            type: GtmEcommerceEventTypeEnum.ClickToProductDetail,
            products: [transformProductToGtmProduct(bundledProduct.value)],
            targetListPosition: 1,
            itemListName: GtmItemListNameEnum.ProductDetailDynamicBundle,
        } as GtmEcommerceEvent

        DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)
    }, 0)

    await safeNavigateTo((event.currentTarget as HTMLAnchorElement).getAttribute('href'))
}
</script>

<template>
    <div class="dynamic-bundle">
        <div class="dynamic-bundle__images">
            <div class="dynamic-bundle__images__container" :data-id="product.id">
                <img :src="product.image" height="70" :alt="product.name" />
                <div v-if="product.productRating && product.productRating.averageRating > 4" class="dynamic-bundle__rating">
                    <img src="/nuxt-assets/images/icons/star-activate.svg" :alt="$t('productDetail.tabs.ratingTitle')" />
                    {{ product.productRating?.averageRating }}
                </div>
            </div>
            <img src="/nuxt-assets/icons/square-plus.svg" width="35" height="35" alt="plus" />
            <a :href="bundledProduct.url" class="dynamic-bundle__images__container" :data-id="product.id" @click.prevent="redirectToUrl">
                <img :src="bundledProduct.image" height="70" :alt="bundledProduct.name" />
                <div v-if="bundledProduct.productRating && bundledProduct.productRating.averageRating > 4" class="dynamic-bundle__rating">
                    <img src="/nuxt-assets/images/icons/star-activate.svg" :alt="$t('productDetail.tabs.ratingTitle')" />
                    {{ bundledProduct.productRating?.averageRating }}
                </div>
            </a>
        </div>
        <div class="dynamic-bundle__names">
            <div v-if="totalDiscount" class="dynamic-bundle__badges">
                <span class="dynamic-bundle__badge">{{ $t('dynamicBundles.discount', { discount: $formatCurrency(totalDiscount) }) }}</span>
            </div>
            {{ product.name }}<br />
            <a :href="bundledProduct.url" class="text-brand" @click.prevent="redirectToUrl">+ {{ bundledProduct.name }}</a>
        </div>
        <div class="dynamic-bundle__prices notranslate">
            <s
                v-if="priceBeforeDiscount > priceTotal"
                class="dynamic-bundle__prices__old notranslate"
                v-html="$formatCurrency(priceBeforeDiscount)"
            ></s>
            <b v-if="priceTotal" class="dynamic-bundle__prices__current notranslate" v-html="$formatCurrency(priceTotal)"></b>
        </div>
        <div class="dynamic-bundle__basket">
            <button class="btn btn-outline-primary btn-rounded-circle btn-md" @click="addToBasket">
                {{ $t('dynamicBundles.cta') }}
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.dynamic-bundle {
    box-shadow: 0 0 19px -8px rgba(0, 0, 0, 0.33);
    padding: 10px;
    display: grid;
    margin: 0 0 1rem;
    grid-gap: 10px;
    grid-template-columns: 2fr 4fr 1fr;
    grid-template-rows: auto;
    grid-template-areas:
        'images images images'
        'names names names'
        'prices prices basket';

    @include screen-md {
        grid-template-areas:
            'images names prices'
            'images names basket';
        box-shadow: none;
        padding: 0;
    }

    &__rating {
        position: absolute;
        bottom: 5px;
        background-color: $color-yellow-light;
        border-radius: 26px;
        padding: 6px 12px;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__badges {
        grid-area: badges;
        align-self: end;
    }

    &__badge {
        color: $color-cta;
        font-size: 12px;
        font-weight: 700;
        line-height: 24px;
        background-color: $color-cta-extra-light;
        border-radius: 100px;
        padding: 0px 20px;
        display: inline-block;
        margin-bottom: 10px;
    }

    &__images {
        grid-area: images;
        display: grid;
        grid-template-columns: 1fr 35px 1fr;
        grid-gap: 10px;
        align-items: center;

        &__container {
            position: relative;
            border-radius: 10px;
            padding: 10px;
            height: 90px;
            align-items: center;
            justify-content: center;
            display: flex;
            color: $color-dark;

            @include screen-md {
                box-shadow: 0 0 19px -8px rgba(0, 0, 0, 0.33);
            }

            img {
                max-height: 70px;
            }
        }
    }

    &__names {
        grid-area: names;
        font-weight: 500;
        margin-top: 10px;
        align-self: center;

        @include screen-md {
            margin-top: 0;
        }

        a {
            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__prices {
        grid-area: prices;
        align-self: center;

        @include screen-md {
            align-self: end;
            justify-content: end;
            text-align: right;
        }

        &__current {
            color: $color-cta;
            font-size: 20px;
            display: block;

            @include screen-md {
                display: inline-block;
                margin-left: 10px;
            }
        }
    }

    &__basket {
        grid-area: basket;
        text-align: right;

        button {
            min-width: 150px;
        }
    }
}

.dynamic-bundles {
    &__wrapper {
        margin: 1rem 0;
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        min-height: 30px;

        @include screen-md {
            justify-content: flex-start;
            margin-bottom: 15px;
        }
    }

    &__title {
        font-size: 16px;
        font-weight: 590;
        line-height: 19px;

        @include screen-md {
            margin-right: 10px;
        }
    }

    &__bundles {
        display: block;

        @include screen-md {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr 1fr;

            padding: 1.5em;
            box-shadow: 0 0 19px -8px rgba(0, 0, 0, 0.33);
            border-radius: 10px;
        }

        .plus-delimiter {
            position: relative;

            @include screen-md {
                margin: 0 55px 0 0;
            }

            &:after {
                display: none;

                @include screen-md {
                    display: block;
                    content: url('/nuxt-assets/icons/square-plus.svg');
                    position: absolute;
                    width: 25px;
                    height: 25px;
                    right: -35px;
                    font-size: 20px;
                    line-height: 24px;
                    border-radius: 5px;
                    top: 50%;
                }
            }
        }
    }

    &__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 5px;
    }

    &__bundles_price {
        align-self: center;
    }
}
</style>
